import { ref, get, update, set } from "firebase/database";
import { db } from "./firebase";
import {
  appendActivity,
  ActivityDomains,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";

/**
 * Feature Flag types and service for Firebase Realtime Database
 */

// App Environment enum matching mobile app
export enum AppEnvironment {
  Sandbox = "sandbox",
  Staging = "staging",
  PreProduction = "preProduction",
  ProductionNewKubernetes = "productionNewKubernetes",
}

// Helper to get display name for environment
export function getEnvironmentDisplayName(env: AppEnvironment): string {
  const names: Record<AppEnvironment, string> = {
    [AppEnvironment.Sandbox]: "Sandbox",
    [AppEnvironment.Staging]: "Staging",
    [AppEnvironment.PreProduction]: "Pre-Production",
    [AppEnvironment.ProductionNewKubernetes]: "Production",
  };
  return names[env];
}

export interface FeatureFlag {
  enabled: boolean;
  min_build?: number;
  platforms?: string[];
  description?: string;
  environment?: string[]; // Array of environment strings (lowercase)
  // Additional optional fields
  [key: string]: any;
}

export interface FeatureFlagsData {
  [flagKey: string]: FeatureFlag;
}

const FEATURE_FLAGS_PATH = "app_config/feature_flags";

/** RTDB key under `app_config/feature_flags`. */
export const HAPPENING_NOW_PARALLAX_FLAG_KEY = "happening_now_parallax";

export function isParallaxEnabledInProduction(
  flag: FeatureFlag | null,
): boolean {
  if (!flag?.environment?.length) return false;
  return flag.environment.includes(AppEnvironment.ProductionNewKubernetes);
}

/** Patch for parallax: `enabled` stays true; only production env membership changes. */
export function buildHappeningNowParallaxProductionPatch(
  flag: FeatureFlag,
  enabledInProduction: boolean,
): Partial<FeatureFlag> {
  const current = flag.environment ?? [];
  const withoutProduction = current.filter(
    (e) => e !== AppEnvironment.ProductionNewKubernetes,
  );
  const environment = enabledInProduction
    ? [...withoutProduction, AppEnvironment.ProductionNewKubernetes]
    : withoutProduction;

  return {
    enabled: true,
    environment,
    platforms: flag.platforms,
    description: flag.description,
    min_build: flag.min_build,
  };
}

export async function getHappeningNowParallaxProductionEnabled(): Promise<boolean> {
  const flag = await getFeatureFlag(HAPPENING_NOW_PARALLAX_FLAG_KEY);
  return isParallaxEnabledInProduction(flag);
}

export async function setHappeningNowParallaxProductionEnabled(
  enabledInProduction: boolean,
  actor?: ActivityActor | null,
): Promise<void> {
  const flag = await getFeatureFlag(HAPPENING_NOW_PARALLAX_FLAG_KEY);
  if (!flag) {
    throw new Error(
      `Feature flag "${HAPPENING_NOW_PARALLAX_FLAG_KEY}" not found`,
    );
  }

  const patch = buildHappeningNowParallaxProductionPatch(
    flag,
    enabledInProduction,
  );
  const errors = validateFeatureFlag(patch);
  if (errors.length > 0) {
    throw new Error(errors.join("\n"));
  }

  await updateFeatureFlag(HAPPENING_NOW_PARALLAX_FLAG_KEY, patch, actor);
}

/** Firebase rejects `undefined` in write payloads; omit those keys. */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Partial<T>;
}

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

/**
 * Get all feature flags
 */
export async function getAllFeatureFlags(): Promise<FeatureFlagsData> {
  try {
    const flagsRef = ref(db, FEATURE_FLAGS_PATH);
    const snapshot = await get(flagsRef);

    if (snapshot.exists()) {
      return snapshot.val() as FeatureFlagsData;
    }
    return {};
  } catch (error) {
    console.error("Error fetching feature flags:", error);
    throw error;
  }
}

/**
 * Get a specific feature flag
 */
export async function getFeatureFlag(
  flagKey: string,
): Promise<FeatureFlag | null> {
  try {
    const flagRef = ref(db, `${FEATURE_FLAGS_PATH}/${flagKey}`);
    const snapshot = await get(flagRef);

    if (snapshot.exists()) {
      return snapshot.val() as FeatureFlag;
    }
    return null;
  } catch (error) {
    console.error(`Error fetching feature flag ${flagKey}:`, error);
    throw error;
  }
}

/**
 * Update a feature flag
 */
export async function updateFeatureFlag(
  flagKey: string,
  updates: Partial<FeatureFlag>,
  actor?: ActivityActor | null,
): Promise<void> {
  const before = actor ? await getFeatureFlag(flagKey) : null;
  try {
    const flagRef = ref(db, `${FEATURE_FLAGS_PATH}/${flagKey}`);
    const payload = omitUndefined(updates as Record<string, unknown>);
    await update(flagRef, payload);
    console.log(`Feature flag ${flagKey} updated:`, payload);
    if (actor) {
      const after = await getFeatureFlag(flagKey);
      await logActivitySafe({
        domain: ActivityDomains.FeatureFlags,
        action: "update",
        resourceType: "feature_flag",
        resourceId: flagKey,
        summary: `Updated feature flag "${flagKey}"`,
        actor,
        details: {
          before: before ?? undefined,
          after: after ?? undefined,
          patch: payload,
        },
      });
    }
  } catch (error) {
    console.error(`Error updating feature flag ${flagKey}:`, error);
    throw error;
  }
}

/**
 * Toggle feature flag enabled status
 */
export async function toggleFeatureFlag(
  flagKey: string,
  actor?: ActivityActor | null,
): Promise<boolean> {
  try {
    const flag = await getFeatureFlag(flagKey);
    if (!flag) {
      throw new Error(`Feature flag ${flagKey} not found`);
    }

    const newEnabledStatus = !flag.enabled;
    const flagRef = ref(db, `${FEATURE_FLAGS_PATH}/${flagKey}`);
    await update(flagRef, { enabled: newEnabledStatus });
    if (actor) {
      await logActivitySafe({
        domain: ActivityDomains.FeatureFlags,
        action: "toggle",
        resourceType: "feature_flag",
        resourceId: flagKey,
        summary: `${newEnabledStatus ? "Enabled" : "Disabled"} feature flag "${flagKey}"`,
        actor,
        details: { enabled: newEnabledStatus },
      });
    }
    return newEnabledStatus;
  } catch (error) {
    console.error(`Error toggling feature flag ${flagKey}:`, error);
    throw error;
  }
}

/**
 * Create a new feature flag
 */
export async function createFeatureFlag(
  flagKey: string,
  flagData: FeatureFlag,
  actor?: ActivityActor | null,
): Promise<void> {
  try {
    const flagRef = ref(db, `${FEATURE_FLAGS_PATH}/${flagKey}`);
    const cleaned = omitUndefined(
      flagData as Record<string, unknown>,
    ) as FeatureFlag;
    await set(flagRef, cleaned);
    console.log(`Feature flag ${flagKey} created`);
    if (actor) {
      await logActivitySafe({
        domain: ActivityDomains.FeatureFlags,
        action: "create",
        resourceType: "feature_flag",
        resourceId: flagKey,
        summary: `Created feature flag "${flagKey}"`,
        actor,
        details: { flag: cleaned },
      });
    }
  } catch (error) {
    console.error(`Error creating feature flag ${flagKey}:`, error);
    throw error;
  }
}

/**
 * Delete a feature flag
 */
export async function deleteFeatureFlag(
  flagKey: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const before = actor ? await getFeatureFlag(flagKey) : null;
  try {
    const flagRef = ref(db, `${FEATURE_FLAGS_PATH}/${flagKey}`);
    await set(flagRef, null);
    console.log(`Feature flag ${flagKey} deleted`);
    if (actor) {
      await logActivitySafe({
        domain: ActivityDomains.FeatureFlags,
        action: "delete",
        resourceType: "feature_flag",
        resourceId: flagKey,
        summary: `Deleted feature flag "${flagKey}"`,
        actor,
        details: { before: before ?? undefined },
      });
    }
  } catch (error) {
    console.error(`Error deleting feature flag ${flagKey}:`, error);
    throw error;
  }
}

/**
 * Validate feature flag data
 */
export function validateFeatureFlag(flag: Partial<FeatureFlag>): string[] {
  const errors: string[] = [];

  if (typeof flag.enabled !== "boolean") {
    errors.push("enabled must be a boolean");
  }

  if (
    flag.min_build !== undefined &&
    (typeof flag.min_build !== "number" || flag.min_build < 0)
  ) {
    errors.push("min_build must be a positive number");
  }

  if (flag.platforms !== undefined) {
    if (!Array.isArray(flag.platforms)) {
      errors.push("platforms must be an array");
    } else if (flag.platforms.length === 0) {
      errors.push("You must select at least one platform");
    } else {
      const validPlatforms = ["ios", "android", "web"];
      const invalidPlatforms = flag.platforms.filter(
        (p) => !validPlatforms.includes(p),
      );
      if (invalidPlatforms.length > 0) {
        errors.push(`Invalid platforms: ${invalidPlatforms.join(", ")}`);
      }
    }
  }

  if (flag.environment !== undefined) {
    if (!Array.isArray(flag.environment)) {
      errors.push("environment must be an array");
    } else if (flag.environment.length > 0) {
      const validEnvironments = Object.values(AppEnvironment);
      const invalidEnvironments = flag.environment.filter(
        (e) => !validEnvironments.includes(e as AppEnvironment),
      );
      if (invalidEnvironments.length > 0) {
        errors.push(`Invalid environments: ${invalidEnvironments.join(", ")}`);
      }
    }
  }

  if (flag.description !== undefined && typeof flag.description !== "string") {
    errors.push("description must be a string");
  }

  return errors;
}
